/**
 * RM-427 — the membership application on the client: which path a sign-up
 * page is, which screens and questions it shows, and the payload it sends.
 *
 * The questions come from the server (GET /application/form), which resolves
 * the question library, the path's defaults and the group's overrides. If that
 * request fails, BUILT_IN_FORM is the form as it stood before RM-427, so no one
 * is ever blocked from applying.
 */

export const STEP_ORDER = ['about', 'details', 'favorites', 'social']

export const STEP_TITLES = {
  about: 'About You',
  details: 'Additional Details',
  favorites: 'Favorite Restaurants',
  social: 'Social Links',
}

/** The sign-up page's flow (loginDialogVerificationType) → its application path. */
export const PATH_FOR_FLOW = {
  activate: 'activate',
  group: 'group',
  gift: 'gift',
  spouse: 'spouse',
  restaurant: 'restaurant',
  'personal-admin': 'personal-admin',
}

const DINING = ['1-2x per week', '1-2x per month', '1-2x per quarter', '1-2x per year']

// Mirrors lib/application/builtInQuestions.js (wizard paths' defaults).
const BUILT_IN_QUESTIONS = [
  { key: 'firstName', step: 'about', type: 'text', label: 'First Name', locked: true },
  { key: 'lastName', step: 'about', type: 'text', label: 'Last Name', locked: true },
  { key: 'email', step: 'about', type: 'email', label: 'Email', locked: true },
  { key: 'phoneNumber', step: 'about', type: 'phone', label: 'Mobile Phone Number', placeholder: '(555) 123-4567', locked: true },
  { key: 'referredBy', step: 'about', type: 'text', label: 'Who referred you?' },
  { key: 'company', step: 'about', type: 'text', label: 'Company' },
  { key: 'title', step: 'about', type: 'text', label: 'Position/Title' },
  { key: 'clubsGroupsAffiliation', step: 'details', type: 'yesno', label: 'Are you a member of any high profile groups?', hint: 'e.g. NFLPA, YPO, Triumph Jets, Golf/Social Clubs' },
  { key: 'clubsGroups', step: 'details', type: 'textarea', label: 'Please share group name(s)', showIf: { key: 'clubsGroupsAffiliation', equals: 'Yes' } },
  { key: 'restaurantAffiliation', step: 'details', type: 'yesno', label: 'Are you affiliated with any restaurants?', hint: 'e.g. Investor, Executive/Partner, VIP/Club Member' },
  { key: 'restaurantAffiliationNames', step: 'details', type: 'textarea', label: 'Please share restaurant name(s)', showIf: { key: 'restaurantAffiliation', equals: 'Yes' } },
  { key: 'diningFrequency', step: 'details', type: 'select', label: 'How often do you dine at popular restaurants?', options: DINING },
  { key: 'favoriteRestaurants', step: 'favorites', type: 'restaurants', label: 'Search for favorite restaurants', hint: 'Please share a few favorites or ones you\'d love reservations to' },
  { key: 'bioPage', step: 'social', type: 'url', label: 'Bio page (company/personal)' },
  { key: 'linkedIn', step: 'social', type: 'text', format: 'linkedin', label: 'LinkedIn profile' },
  { key: 'instagram', step: 'social', type: 'url', format: 'instagram', label: 'Instagram profile' },
  { key: 'x', step: 'social', type: 'url', format: 'x', label: 'X profile' },
]

/** The form a path had before RM-427 — used only if the server's can't load. */
export function builtInForm(path) {
  const personalAdmin = path === 'personal-admin'
  const questions = BUILT_IN_QUESTIONS
    .filter(q => !personalAdmin || q.locked)
    .map(q => ({ hint: '', placeholder: '', options: [], format: null, showIf: null, ...q, required: !!q.locked || (!personalAdmin && q.step !== 'social') }))

  return {
    path,
    group: null,
    steps: STEP_ORDER.filter(s => questions.some(q => q.step === s)),
    questions,
    smsOptIn: { enabled: false },
  }
}

/** Whether a question is asked, given the answers so far (its showIf). */
export function isAsked(question, answers) {
  if (!question.showIf || !question.showIf.key)
    return true

  return (answers || {})[question.showIf.key] === question.showIf.equals
}

export function questionsForStep(form, step, answers) {
  return ((form && form.questions) || []).filter(q => q.step === step && isAsked(q, answers))
}

export function hasAnswer(question, value) {
  if (question.type === 'restaurants')
    return Array.isArray(value) && value.length > 0
  if (value === null || value === undefined)
    return false

  return String(value).trim() !== ''
}

// ── input normalizers (moved unchanged from loginJoinDialog) ─────────────────

export function normalizeLinkedIn(raw) {
  if (!raw || !raw.trim())
    return raw
  let value = raw.trim()
  const prefixes = [
    'https://www.linkedin.com/in/',
    'http://www.linkedin.com/in/',
    'https://linkedin.com/in/',
    'http://linkedin.com/in/',
    'www.linkedin.com/in/',
    'linkedin.com/in/',
  ]
  for (const prefix of prefixes) {
    if (value.toLowerCase().startsWith(prefix.toLowerCase())) {
      value = value.substring(prefix.length)
      break
    }
  }

  return value.replace(/\/+$/, '').replace(/^\/+/, '')
}

export function normalizeInstagram(raw) {
  if (!raw || !raw.trim())
    return raw
  const trimmed = raw.trim()
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    if (trimmed.startsWith('www.instagram.com/') || trimmed.startsWith('instagram.com/')) {
      const host = trimmed.startsWith('www.') ? trimmed : `www.${trimmed}`

      return `https://${host}`
    }
    const username = trimmed.startsWith('@') ? trimmed.substring(1) : trimmed

    return `https://www.instagram.com/${username}`
  }
  if (trimmed.startsWith('http://'))
    return trimmed.replace('http://', 'https://')

  return raw
}

export function normalizeX(raw) {
  if (!raw || !raw.trim())
    return raw
  let trimmed = raw.trim()
  if (trimmed.includes('twitter.com'))
    trimmed = trimmed.replace('twitter.com', 'x.com')
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    if (trimmed.startsWith('x.com/'))
      return `https://${trimmed}`
    const username = trimmed.startsWith('@') ? trimmed.substring(1) : trimmed

    return `https://x.com/${username}`
  }
  if (trimmed.startsWith('http://'))
    return trimmed.replace('http://', 'https://')

  return trimmed
}

export const NORMALIZERS = { linkedin: normalizeLinkedIn, instagram: normalizeInstagram, x: normalizeX }

/** (123) 456-7890 as the member types (moved unchanged from loginJoinDialog). */
export function formatPhone(raw) {
  if (!raw)
    return raw
  let digits = String(raw).replace(/\D/g, '')
  if (digits.length > 10 && digits.startsWith('1'))
    digits = digits.substring(1)
  digits = digits.substring(0, 10)
  if (digits.length === 0)
    return ''
  if (digits.length <= 3)
    return `(${digits}`
  if (digits.length <= 6)
    return `(${digits.substring(0, 3)}) ${digits.substring(3)}`

  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
}

/**
 * What the application sends: every asked question's answer by key
 * (`applicationAnswers`), and the same built-in answers under the field names
 * the server has always read, so a profile is saved even if the server can't
 * load the question library.
 */
export function applicationPayload(form, answers) {
  const asked = ((form && form.questions) || []).filter(q => isAsked(q, answers))
  const applicationAnswers = {}
  asked.forEach(q => {
    const v = answers[q.key]
    if (hasAnswer(q, v))
      applicationAnswers[q.key] = v
  })
  const a = applicationAnswers
  const favorites = Array.isArray(a.favoriteRestaurants) ? a.favoriteRestaurants : []

  return {
    applicationAnswers,
    firstName: a.firstName,
    lastName: a.lastName,
    email: a.email,
    phoneNumber: a.phoneNumber,
    referredBy: a.referredBy,
    company: a.company,
    title: a.title,
    bioPage: a.bioPage,
    profileLinkedInProfileId: a.linkedIn,
    profileInstagramProfileId: a.instagram,
    profileXProfileId: a.x,
    clubsGroupsAffiliation: a.clubsGroupsAffiliation,
    clubsGroups: a.clubsGroups,
    restaurantAffiliation: a.restaurantAffiliation,
    restaurantAffiliationNames: a.restaurantAffiliationNames,
    diningFrequency: a.diningFrequency,
    userCities: favorites.map(r => `${r.name} (${r.market})`).join(', '),
    userFavoriteRestaurantIds: favorites.map(r => r._id).filter(Boolean),
  }
}
